import { useMemo, useState } from "react";
import { Copy, Download, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type AggregatedTransaction,
  SCHEDULE_E_CATEGORIES,
  useAccounting,
} from "@/hooks/use-accounting";
import { fmtMoney } from "@/components/accounting/accounting-format";

interface Props {
  year: number;
}

const catLabel = (key: string) => SCHEDULE_E_CATEGORIES.find((c) => c.key === key)?.label ?? key;
const catLine = (key: string) => SCHEDULE_E_CATEGORIES.find((c) => c.key === key)?.line ?? "";

/** Filesystem-safe slug for a property label used in CSV filenames. */
export const slugifyLabel = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "property";

export function buildCSV(txns: AggregatedTransaction[]): string {
  const header = ["Date", "Property", "Category", "Schedule E Line", "Type", "Amount", "Source", "Description"];
  const esc = (v: string | number) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = txns.map((t) =>
    [
      t.date,
      t.property_label ?? "Portfolio-wide",
      catLabel(t.category),
      catLine(t.category),
      t.type,
      (t.type === "income" ? "" : "-") + t.amount,
      t.source_label ?? t.source,
      t.description,
    ]
      .map(esc)
      .join(","),
  );
  return [header.join(","), ...rows].join("\n");
}

interface ReportArgs {
  year: number;
  txns: AggregatedTransaction[];
  properties: { id: string; label: string }[];
  totalIncome: number;
  totalExpenses: number;
  netIncome: number;
}

function buildReport({ year, txns, properties, totalIncome, totalExpenses, netIncome }: ReportArgs): string {
  const head = [
    "SYNTRA RENTAL PORTFOLIO — SCHEDULE E TAX SUMMARY",
    `Tax year: ${year}`,
    `Properties: ${properties.length}`,
    "".padEnd(60, "="),
    "",
  ];

  const summary = [
    "PORTFOLIO TOTALS",
    "".padEnd(60, "-"),
    `  Rents received          ${fmtMoney(totalIncome).padStart(16)}`,
    `  Total deductions        ${fmtMoney(-totalExpenses).padStart(16)}`,
    `  Net rental income       ${fmtMoney(netIncome).padStart(16)}`,
    "",
    "EXPENSE BREAKDOWN BY SCHEDULE E LINE",
    "".padEnd(60, "-"),
  ];
  const byCat: Record<string, number> = {};
  txns.filter((t) => t.type === "expense").forEach((t) => {
    byCat[t.category] = (byCat[t.category] || 0) + t.amount;
  });
  SCHEDULE_E_CATEGORIES.filter((c) => c.type === "expense").forEach((c) => {
    const amt = byCat[c.key] || 0;
    if (amt > 0) summary.push(`  Line ${String(c.line).padStart(2)}  ${c.label.padEnd(28)} ${fmtMoney(amt).padStart(14)}`);
  });
  summary.push("");

  const propBlocks = properties.map((p) => {
    const ptxns = txns.filter((t) => t.pipeline_property_id === p.id);
    const pInc = ptxns.filter((t) => t.type === "income").reduce((s, t) => s + t.amount, 0);
    const pExp = ptxns.filter((t) => t.type === "expense").reduce((s, t) => s + t.amount, 0);
    const lines = [`PROPERTY · ${p.label.toUpperCase()}`, "".padEnd(60, "-")];
    SCHEDULE_E_CATEGORIES.forEach((c) => {
      const amt = ptxns.filter((t) => t.category === c.key).reduce((s, t) => s + t.amount, 0);
      if (amt > 0) {
        lines.push(`  Line ${String(c.line).padStart(2)}  ${c.label.padEnd(28)} ${fmtMoney(c.type === "income" ? amt : -amt).padStart(14)}`);
      }
    });
    lines.push("", `  Net for property         ${fmtMoney(pInc - pExp).padStart(14)}`, "");
    return lines.join("\n");
  });

  const footer = [
    "".padEnd(60, "="),
    "NOTES",
    "  · Income from recorded rent payments.",
    "  · Mortgage interest computed from active financing terms (year estimate).",
    "  · Depreciation: 27.5-yr straight line on 80% of purchase price.",
    "  · Repairs include completed rehab spend recorded in this calendar year.",
    "  · Manual transactions you logged appear under their assigned category.",
    "",
    "Generated by Syntra · Confirm with your CPA before filing.",
  ];

  return [...head, ...summary, ...propBlocks, ...footer].join("\n");
}

function downloadFile(content: string, filename: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(url);
  a.remove();
}

export function ExportTab({ year }: Props) {
  const { allTransactions, properties, totalIncome, totalExpenses, netIncome } = useAccounting(year);
  const [scope, setScope] = useState("all");

  const report = useMemo(
    () => buildReport({ year, txns: allTransactions, properties, totalIncome, totalExpenses, netIncome }),
    [year, allTransactions, properties, totalIncome, totalExpenses, netIncome],
  );

  const preview = useMemo(() => {
    const lines = report.split("\n");
    return lines.slice(0, 30).join("\n") + (lines.length > 30 ? "\n…" : "");
  }, [report]);

  const handleCsv = () => {
    if (scope === "all") {
      downloadFile(buildCSV(allTransactions), `syntra-transactions-${year}.csv`, "text/csv;charset=utf-8");
      toast.success("Transactions CSV downloaded");
      return;
    }
    const prop = properties.find((p) => p.id === scope);
    const rows = allTransactions.filter((t) => t.pipeline_property_id === scope);
    if (!rows.length) {
      toast.error(`No ${year} transactions for ${prop?.label ?? "this property"}`);
      return;
    }
    downloadFile(
      buildCSV(rows),
      `syntra-transactions-${slugifyLabel(prop?.label ?? scope)}-${year}.csv`,
      "text/csv;charset=utf-8",
    );
    toast.success(`CSV downloaded for ${prop?.label ?? "property"}`);
  };

  const handleCsvEach = () => {
    const groups = properties
      .map((p) => ({ label: p.label, rows: allTransactions.filter((t) => t.pipeline_property_id === p.id) }))
      .filter((g) => g.rows.length);
    if (!groups.length) {
      toast.error("No property transactions to export");
      return;
    }
    groups.forEach((g) =>
      downloadFile(
        buildCSV(g.rows),
        `syntra-transactions-${slugifyLabel(g.label)}-${year}.csv`,
        "text/csv;charset=utf-8",
      ),
    );
    toast.success(`Downloaded ${groups.length} property CSV${groups.length > 1 ? "s" : ""}`);
  };
  const handleReport = () => {
    downloadFile(report, `syntra-schedule-e-${year}.txt`, "text/plain;charset=utf-8");
    toast.success("CPA report downloaded");
  };
  const handleCopy = () => {
    navigator.clipboard?.writeText(report).then(
      () => toast.success("Report copied to clipboard"),
      () => toast.error("Could not copy to clipboard"),
    );
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card className="flex flex-col gap-3 p-5">
          <span className="grid h-11 w-11 place-items-center rounded-lg bg-primary/10 text-primary">
            <FileSpreadsheet className="h-5 w-5" />
          </span>
          <h3 className="text-base font-bold text-foreground">Transactions CSV</h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            One row per transaction — date, property, category, Schedule E line, amount. Drop straight into Excel or
            hand to your CPA's tax software.
          </p>
          <ul className="list-disc space-y-1 pl-4 text-xs text-muted-foreground">
            <li>Includes every auto-aggregated and manual entry</li>
            <li>Schedule E line number on each row</li>
            <li>Export the whole portfolio or one property at a time</li>
            <li>UTF-8, comma-separated, RFC 4180 quoted</li>
          </ul>
          <div className="mt-1 space-y-2">
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger aria-label="Choose which property to export">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All properties (combined)</SelectItem>
                {properties.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleCsv}>
                <Download className="mr-1.5 h-4 w-4" /> Download CSV
              </Button>
              {properties.length > 1 && (
                <Button variant="outline" onClick={handleCsvEach}>
                  <Download className="mr-1.5 h-4 w-4" /> One file per property
                </Button>
              )}
            </div>
          </div>
        </Card>

        <Card className="flex flex-col gap-3 p-5">
          <span className="grid h-11 w-11 place-items-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </span>
          <h3 className="text-base font-bold text-foreground">CPA-ready report</h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Formatted text report with KPIs, per-property Schedule E line items, and net rental income — ready to
            email or print for your accountant.
          </p>
          <ul className="list-disc space-y-1 pl-4 text-xs text-muted-foreground">
            <li>Schedule E line totals across every property</li>
            <li>Per-property breakdown with source attribution</li>
            <li>Plain text — opens anywhere, no formatting drift</li>
          </ul>
          <div className="mt-1 flex flex-wrap gap-2">
            <Button onClick={handleReport}>
              <Download className="mr-1.5 h-4 w-4" /> Download report
            </Button>
            <Button variant="outline" onClick={handleCopy}>
              <Copy className="mr-1.5 h-4 w-4" /> Copy to clipboard
            </Button>
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <div className="mb-3">
          <h3 className="text-sm font-bold text-foreground">Report preview</h3>
          <p className="text-xs text-muted-foreground">First chunk of what you'll send your CPA</p>
        </div>
        <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-muted/40 p-3.5 font-mono text-[11.5px] leading-relaxed text-muted-foreground">
          {preview}
        </pre>
      </Card>
    </div>
  );
}
